import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, Switch, useHistory, useLocation, useParams } from 'react-router-dom';
import PortfolioDashboard from '../components/Consultant/PortfolioDashboard';
import ConsultantClientDetail from '../components/Consultant/ConsultantClientDetail';
import AddConsultantClientWizard from '../components/Consultant/AddConsultantClientWizard';
import OnboardExistingClientWizard from '../components/Consultant/OnboardExistingClientWizard';
import AmendConsultantClientModal from '../components/Consultant/AmendConsultantClientModal';
import OperationalDashboard from '../components/Consultant/OperationalDashboard';
import ClientHealthDashboard from '../components/Consultant/ClientHealthDashboard';
import ImpactWinsDashboard from '../components/Consultant/ImpactWinsDashboard';
import WorkItemsMissionControl from '../components/Consultant/WorkItemsMissionControl';
import ConsultantBillingOverview from '../components/Consultant/ConsultantBillingOverview';
import { Actions as CONSULTANT_ACTIONS } from '../store/actions/consultantClientsActions';
import { Actions as DASHBOARD_ACTIONS } from '../store/actions/consultantDashboardActions';
import { Actions as ORG_ACTIONS } from '../store/actions/organisationActions';
import { toggleAlertMessage } from '../store/action';
import { filterConsultantClients } from '../components/utils/consultantClientUtils';
import { getFirmId, isConsultantFirmOrg } from '../components/utils/managementShell';

const ClientHealthRoute = ({
  getClientHealth,
  health,
  loading,
  openClientWorkspace,
  setRespondingPartyAccess,
  createWorkItem,
}) => {
  const { id } = useParams();
  const history = useHistory();

  useEffect(() => {
    if (id) getClientHealth(id);
  }, [id, getClientHealth]);

  return (
    <ClientHealthDashboard
      health={health}
      loading={loading}
      onBack={() => history.push('/consultant/dashboard')}
      onRefresh={() => getClientHealth(id, { refresh: true })}
      onOpenBilling={() => history.push(`/consultant/client/${id}`)}
      onOpenWorkspace={() => openClientWorkspace(id)}
      setRespondingPartyAccess={setRespondingPartyAccess}
      createWorkItem={createWorkItem}
      onOpenTask={(taskId) => history.push(`/consultant/tasks/${taskId}`)}
    />
  );
};

const ConsultantPortfolio = ({
  consultantParentId,
  clients,
  renewals,
  schedules,
  masterPerReportFeeVal,
  programme,
  catalog,
  pendingContracts,
  loading,
  actionLoading,
  getConsultantClients,
  getConsultantRenewals,
  getConsultantSchedules,
  hydratePendingContracts,
  activateConsultantClient,
  resendConsultantClientContract,
  voidConsultantClientContract,
  clearPendingContract,
  showAlert,
  dashboard,
  health,
  impact,
  ownerFilter,
  dashboardLoading,
  organisationUsers,
  profile,
  getConsultantDashboard,
  getClientHealth,
  getConsultantImpact,
  setOwnerFilter,
  setRespondingPartyAccess,
  createWorkItem,
  updateOrganisation,
  setCurrentOrganisation,
  getOrganisation,
}) => {
  const history = useHistory();
  const location = useLocation();
  const [showAddWizard, setShowAddWizard] = useState(false);
  const [showOnboardWizard, setShowOnboardWizard] = useState(false);
  const [amendTarget, setAmendTarget] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('add') === '1') {
      setShowAddWizard(true);
      history.replace('/consultant/portfolio');
    }
  }, [location.search, history]);

  useEffect(() => {
    // Wait for firm context — early fetches send Organisation: "false" and 403.
    if (!consultantParentId) return;
    hydratePendingContracts();
    getConsultantClients(true);
    getConsultantRenewals();
    getConsultantSchedules();
  }, [
    consultantParentId,
    getConsultantClients,
    getConsultantRenewals,
    getConsultantSchedules,
    hydratePendingContracts,
  ]);

  useEffect(() => {
    // Wait until firm context exists — early fetch sends Organisation: "false" and 403s.
    if (!consultantParentId) return;
    if (
      location.pathname === '/consultant/dashboard' ||
      location.pathname === '/consultant' ||
      location.pathname.startsWith('/consultant/tasks')
    ) {
      getConsultantDashboard({ ownerUserId: ownerFilter || undefined });
    }
    if (location.pathname === '/consultant/impact') {
      getConsultantImpact({ ownerUserId: ownerFilter || undefined });
    }
  }, [
    location.pathname,
    ownerFilter,
    consultantParentId,
    getConsultantDashboard,
    getConsultantImpact,
  ]);

  const clientRows = useMemo(
    () => filterConsultantClients(clients, consultantParentId),
    [clients, consultantParentId],
  );

  const openClientWorkspace = (clientOrgId) => {
    const client =
      clients.find((c) => c.id === clientOrgId) ||
      clientRows.find((c) => c.id === clientOrgId) ||
      { id: clientOrgId };
    updateOrganisation(client);
    setCurrentOrganisation(client);
    localStorage.setItem('Organisation', clientOrgId);
    if (client.name || client.accountName) {
      localStorage.setItem('orgDetails', JSON.stringify(client));
    }
    getOrganisation();
    history.push('/');
  };

  const handleRefresh = () => {
    getConsultantClients(true);
    getConsultantRenewals();
    getConsultantSchedules();
  };

  const handleActivate = async (orgId) => {
    const result = await activateConsultantClient(orgId);
    showAlert(true, result.ok ? 'Client activated.' : result.message, result.ok ? 'success' : 'error', 5000);
  };

  const handleResend = async (orgId, contractId) => {
    const result = await resendConsultantClientContract(contractId, { rotateToken: true });
    if (result.ok) {
      showAlert(true, 'Invitation resent.', 'success', 4000);
      if (result.data?.agreementUrl) window.open(result.data.agreementUrl, '_blank', 'noopener');
    } else {
      showAlert(true, result.message, 'error', 5000);
    }
  };

  const handleVoid = async (orgId, contractId) => {
    const reason = window.prompt('Reason for voiding this invitation (optional):') || 'Voided by consultant';
    const result = await voidConsultantClientContract(contractId, { reason });
    if (result.ok) {
      clearPendingContract(orgId);
      showAlert(true, 'Invitation voided.', 'success', 4000);
      handleRefresh();
    } else {
      showAlert(true, result.message, 'error', 5000);
    }
  };

  return (
    <div className='p-6'>
      <Switch>
        <Route exact path='/consultant'>
          <Redirect to='/consultant/dashboard' />
        </Route>
        <Route exact path='/consultant/dashboard'>
          <OperationalDashboard
            dashboard={dashboard}
            loading={!!dashboardLoading.dashboard}
            ownerFilter={ownerFilter}
            profileDetails={profile}
            organisationUsers={organisationUsers}
            onOwnerFilterChange={(value) => {
              setOwnerFilter(value || null);
              getConsultantDashboard({ ownerUserId: value || undefined });
            }}
            onRefresh={() => getConsultantDashboard({ ownerUserId: ownerFilter || undefined, refresh: true })}
          />
        </Route>
        <Route exact path='/consultant/tasks'>
          <WorkItemsMissionControl />
        </Route>
        <Route exact path='/consultant/tasks/:id'>
          <WorkItemsMissionControl />
        </Route>
        <Route exact path='/consultant/impact'>
          <ImpactWinsDashboard
            impact={impact}
            loading={!!dashboardLoading.impact}
            onRefresh={() => getConsultantImpact({ ownerUserId: ownerFilter || undefined, refresh: true })}
          />
        </Route>
        <Route exact path='/consultant/billing'>
          <ConsultantBillingOverview
            programme={programme}
            catalog={catalog}
            onViewAgreements={() => history.push('/agreements')}
          />
        </Route>
        <Route path='/consultant/client/:id/health'>
          <ClientHealthRoute
            getClientHealth={getClientHealth}
            health={health}
            loading={!!dashboardLoading.health}
            openClientWorkspace={openClientWorkspace}
            setRespondingPartyAccess={setRespondingPartyAccess}
            createWorkItem={createWorkItem}
          />
        </Route>
        <Route path='/consultant/client/:id'>
          <ConsultantClientDetail
            clients={clientRows}
            renewals={renewals}
            schedules={schedules}
            masterPerReportFeeVal={masterPerReportFeeVal}
            onBack={() => history.push('/consultant/portfolio')}
          />
        </Route>
        <Route path='/consultant/portfolio'>
          <PortfolioDashboard
            clients={clients}
            renewals={renewals}
            schedules={schedules}
            masterPerReportFeeVal={masterPerReportFeeVal}
            pendingContracts={pendingContracts}
            consultantParentId={consultantParentId}
            loading={!!loading.clients}
            actionLoading={actionLoading}
            onAddClient={() => setShowAddWizard(true)}
            onOnboardExisting={() => setShowOnboardWizard(true)}
            onActivate={handleActivate}
            onResend={handleResend}
            onVoid={handleVoid}
            onAmend={(client) => setAmendTarget(client)}
            onRefresh={handleRefresh}
          />
        </Route>
      </Switch>

      {showAddWizard && (
        <AddConsultantClientWizard
          open={showAddWizard}
          onClose={() => setShowAddWizard(false)}
          onSuccess={() => {
            setShowAddWizard(false);
            handleRefresh();
          }}
        />
      )}

      {showOnboardWizard && (
        <OnboardExistingClientWizard
          open={showOnboardWizard}
          onClose={() => setShowOnboardWizard(false)}
          onSuccess={() => {
            setShowOnboardWizard(false);
            handleRefresh();
          }}
        />
      )}

      {amendTarget && (
        <AmendConsultantClientModal
          open={!!amendTarget}
          client={amendTarget}
          schedule={schedules.find((schedule) => schedule.organisationId === amendTarget.id)}
          programme={programme}
          catalog={catalog}
          onClose={() => setAmendTarget(null)}
          onSuccess={() => {
            setAmendTarget(null);
            handleRefresh();
          }}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  const profile = state.profile?.details;
  const org = state.organisation?.details;
  const consultantParentId =
    (typeof profile?.consultantAdmin === 'string' && profile.consultantAdmin) ||
    (isConsultantFirmOrg(org) ? org.id : null) ||
    getFirmId(org, profile);

  return {
    consultantParentId,
    clients: state.consultantClients?.clients || [],
    renewals: state.consultantClients?.renewals || [],
    schedules: state.consultantClients?.schedules || [],
    masterPerReportFeeVal: state.consultantClients?.masterPerReportFeeVal,
    programme: state.consultantClients?.programme,
    catalog: state.consultantClients?.catalog,
    pendingContracts: state.consultantClients?.pendingContracts || {},
    loading: state.consultantClients?.loading || {},
    actionLoading: state.consultantClients?.loading || {},
    dashboard: state.consultantDashboard?.dashboard,
    health: state.consultantDashboard?.health,
    impact: state.consultantDashboard?.impact,
    ownerFilter: state.consultantDashboard?.ownerFilter,
    dashboardLoading: state.consultantDashboard?.loading || {},
    organisationUsers: state.organisation?.users || [],
    profile: state.profile?.details || null,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getConsultantClients: (includeAll) => dispatch(CONSULTANT_ACTIONS.getConsultantClients(includeAll)),
  getConsultantRenewals: () => dispatch(CONSULTANT_ACTIONS.getConsultantRenewals()),
  getConsultantSchedules: () => dispatch(CONSULTANT_ACTIONS.getConsultantSchedules()),
  hydratePendingContracts: () => dispatch(CONSULTANT_ACTIONS.hydratePendingContracts()),
  activateConsultantClient: (orgId) => dispatch(CONSULTANT_ACTIONS.activateConsultantClient(orgId)),
  resendConsultantClientContract: (contractId, body) =>
    dispatch(CONSULTANT_ACTIONS.resendConsultantClientContract(contractId, body)),
  voidConsultantClientContract: (contractId, body) =>
    dispatch(CONSULTANT_ACTIONS.voidConsultantClientContract(contractId, body)),
  clearPendingContract: (orgId) => dispatch(CONSULTANT_ACTIONS.clearPendingContract(orgId)),
  showAlert: (status, message, severity, hideDuration) =>
    dispatch(toggleAlertMessage(status, message, severity, hideDuration)),
  getConsultantDashboard: (opts) => dispatch(DASHBOARD_ACTIONS.getConsultantDashboard(opts)),
  getClientHealth: (orgId, opts) => dispatch(DASHBOARD_ACTIONS.getClientHealth(orgId, opts)),
  getConsultantImpact: (opts) => dispatch(DASHBOARD_ACTIONS.getConsultantImpact(opts)),
  setOwnerFilter: (ownerUserId) => dispatch(DASHBOARD_ACTIONS.setOwnerFilter(ownerUserId)),
  setRespondingPartyAccess: (clientOrgId, enabled) =>
    dispatch(DASHBOARD_ACTIONS.setRespondingPartyAccess(clientOrgId, enabled)),
  createWorkItem: (orgId, body) => dispatch(DASHBOARD_ACTIONS.createWorkItem(orgId, body)),
  updateOrganisation: (organisation) => dispatch(ORG_ACTIONS.updateOrganisation(organisation)),
  setCurrentOrganisation: (organisation) => dispatch(ORG_ACTIONS.setCurrentOrganisation(organisation)),
  getOrganisation: () => dispatch(ORG_ACTIONS.getOrganisation()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ConsultantPortfolio);
